import { capitalize } from "lodash-es";
import { v4 as uuidV4 } from "uuid";

import {
  FileExtractionConfig,
  JSONSchemaPropertyDefinition,
  ManifestIntegrationProvider,
  ManifestJSONSchema,
  ManifestResourceDefinition,
} from "src/api/connectApi/manifestTypes";
import { isFieldOfNullableType } from "src/connections/config/manifest/utils";
import { CommonOutputMappingKey } from "src/integrationNode/integrationResources/common";
import { getDefaultOutputMapping } from "src/integrationNode/integrationResources/DefaultResourceConfig";
import {
  DEFAULT_OUTPUT_BE_NAME,
  DropDownInput,
  InputMapping,
  InputMappingBE,
  InputMappingGroup,
  InputMappingList,
  InputMappings,
  OutputMapping,
  OutputMappingBE,
  OutputMappings,
} from "src/integrationNode/types";
import {
  inputMappingsFeToBe,
  multiselectsFeToBe,
} from "src/integrationNode/utils";
import {
  getDefaultManifestInputGroups,
  getDefaultManifestInputUngrouped,
  isInputComplexType,
} from "src/manifestConnectionNode/manifestConnectionResources/DefaultResources";
import {
  ManifestIntegrationResource,
  ManifestIntegrationResourceBE,
} from "src/manifestConnectionNode/types";

export const NON_HTTP_CONFIGURE_REQUEST_TITLE = "Configure request";
export const NON_HTTP_CONFIGURE_RESPONSE_TITLE = "Configure response";

const NON_HTTP_BUNDLE_RESPONSE_TITLE = "Bundle response";
const NON_HTTP_BUNDLE_RESPONSE_HINT =
  "Add the entire response into the data object";

export const NON_HTTP_ERROR_4XX_TITLE = "Do not error with client errors";
export const NON_HTTP_ERROR_4XX_HINT =
  "In case of client errors, the Decision Flow will continue executing";

export const NON_HTTP_ERROR_5XX_TITLE = "Do not error with server errors";
export const NON_HTTP_ERROR_5XX_HINT =
  "In case of server errors, the Decision Flow will continue executing";

const DEFAULT_INPUT_CONTENT = {
  hint: undefined,
  rules: { required: false },
  type: "text",
};

const manifestIntegrationResourceInputBeToFe = (
  resourceInputBE: InputMappingBE,
): InputMappings => {
  /**
   * Display name, hint, rules, and getDefaultElements are defaulted here and the InputMappings
   * are filled from the manifest when the node editor is rendered.
   * */
  const resourceInputFe: InputMappings = {
    ungrouped: {},
    grouped: {},
    lists: {},
  };
  Object.entries(resourceInputBE.singles).forEach(
    ([singleName, singleMapping]) => {
      resourceInputFe.ungrouped[singleName] = {
        ...DEFAULT_INPUT_CONTENT,
        id: singleMapping.id,
        displayName: singleName,
        assignedTo: singleMapping.expression,
      } as InputMapping;
    },
  );
  Object.entries(resourceInputBE.groups).forEach(
    ([groupName, groupMapping]) => {
      resourceInputFe.grouped[groupName] = {
        displayName: groupName,
        rules: { required: false },
        getDefaultElements: () => ({}),
      };
      Object.entries(groupMapping.singles).forEach(
        ([singleName, singleMapping]) => {
          if (!resourceInputFe.grouped[groupName].elements) {
            resourceInputFe.grouped[groupName].elements = {};
          }
          resourceInputFe.grouped[groupName].elements![singleName] = {
            ...DEFAULT_INPUT_CONTENT,
            id: singleMapping.id,
            displayName: singleName,
            assignedTo: singleMapping.expression,
          } as InputMapping;
        },
      );
    },
  );
  Object.entries(resourceInputBE.lists).forEach(([listName, listMappings]) => {
    resourceInputFe.lists[listName] = {
      displayName: listName,
      rules: { required: false },
      getDefaultElement: () => ({}),
      elements: [],
    };
    for (const listMapping of listMappings) {
      const listElements: { [key: string]: InputMapping } = {};
      Object.entries(listMapping.singles).forEach(
        ([singleName, singleMapping]) => {
          listElements[singleName] = {
            ...DEFAULT_INPUT_CONTENT,
            id: singleMapping.id,
            displayName: singleName,
            assignedTo: singleMapping.expression,
          } as InputMapping;
        },
      );
      resourceInputFe.lists[listName].elements.push(listElements);
    }
  });
  return resourceInputFe;
};

export const getCommonManifestOutputMappings = (): {
  [key: string]: OutputMapping;
} => ({
  [CommonOutputMappingKey.PROVIDER_RESPONSE_TIME]: {
    id: uuidV4(),
    displayName: "Provider latency",
    assignedTo: "",
    selected: false,
    hint: "The time in milliseconds it took for the provider to process the request",
  },
});

const manifestIntegrationResourceOutputBeToFe = (resourceOutputBE: {
  [DEFAULT_OUTPUT_BE_NAME]: OutputMappingBE;
  [key: string]: OutputMappingBE;
}): OutputMappings => {
  /**
   * Display name and hint are defaulted here and the OutputMappings
   * are filled from the manifest when the node editor is rendered.
   * */
  const resourceOutputFe: OutputMappings = {
    [DEFAULT_OUTPUT_BE_NAME]: getDefaultOutputMapping(),
    insights: getCommonManifestOutputMappings(),
  };

  Object.entries(resourceOutputBE).forEach(([key, value]) => {
    if (key !== DEFAULT_OUTPUT_BE_NAME) {
      resourceOutputFe.insights[key] = {
        id: value.id,
        assignedTo: value.mapped_to,
        selected: value.active,
        displayName: resourceOutputFe.insights[key]?.displayName || "",
        hint: resourceOutputFe.insights[key]?.hint || undefined,
      };
    } else {
      resourceOutputFe[DEFAULT_OUTPUT_BE_NAME].id = value.id;
      resourceOutputFe[DEFAULT_OUTPUT_BE_NAME].assignedTo = value.mapped_to;
      resourceOutputFe[DEFAULT_OUTPUT_BE_NAME].selected = value.active;
    }
  });
  return resourceOutputFe;
};

export const manifestIntegrationResourceBeToFe = (
  reportBE: ManifestIntegrationResourceBE,
): ManifestIntegrationResource => {
  const files = Object.fromEntries(
    Object.entries(reportBE.files || {}).map(([key, value]) => [
      key,
      {
        id: value.id,
        displayName: "File",
        assignedTo: value.mapped_to,
        selected: value.active,
      },
    ]),
  );
  const manifestIntegrationResource: ManifestIntegrationResource = {
    connectionId: reportBE.connection_id,
    resourceConfigId: reportBE.resource_config_id,
    providerResource: reportBE.provider_resource,
    input: manifestIntegrationResourceInputBeToFe(reportBE.input),
    output: manifestIntegrationResourceOutputBeToFe(reportBE.output),
    files,
    config: reportBE.config,
  };
  return manifestIntegrationResource;
};

export const manifestIntegrationResourceFeToBe = (
  resourceFE: ManifestIntegrationResource,
): ManifestIntegrationResourceBE => {
  return {
    provider_resource: resourceFE.providerResource,
    connection_id: resourceFE.connectionId,
    resource_config_id: resourceFE.resourceConfigId,
    config: resourceFE.config,
    input: {
      singles: {
        ...inputMappingsFeToBe(resourceFE.input.ungrouped),
        ...multiselectsFeToBe(resourceFE.input.multiselectors),
      },
      groups: Object.fromEntries(
        Object.entries(resourceFE.input.grouped)
          .map(([groupName, group]) => [
            groupName,
            group.elements
              ? {
                  singles: inputMappingsFeToBe(group.elements),
                  groups: {},
                  lists: {},
                }
              : undefined,
          ])
          .filter(([, value]) => value),
      ),
      lists: Object.fromEntries(
        Object.entries(resourceFE.input.lists).map(([listName, list]) => [
          listName,
          list.elements.map((element) => ({
            singles: inputMappingsFeToBe(element),
            groups: {},
            lists: {},
          })),
        ]),
      ),
    },
    output: {
      [DEFAULT_OUTPUT_BE_NAME]: {
        id: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].id,
        mapped_to: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].assignedTo,
        active: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].selected,
      },
      ...Object.keys(resourceFE.output.insights).reduce(
        (acc: { [key: string]: OutputMappingBE }, beName) => {
          const outputMapping = resourceFE.output.insights[beName];
          acc[beName] = {
            id: outputMapping.id,
            mapped_to: outputMapping.assignedTo,
            active: outputMapping.selected,
          };
          return acc;
        },
        {},
      ),
    },
    files: Object.fromEntries(
      Object.entries(resourceFE.files || {}).map(([key, value]) => [
        key,
        { id: value.id, mapped_to: value.assignedTo, active: value.selected },
      ]),
    ),
  } as ManifestIntegrationResourceBE;
};

const fillElementFromManifest = (
  elementKey: string,
  element: InputMapping,
  elementSchema: JSONSchemaPropertyDefinition,
  requiredElements: string[] | undefined,
) => {
  const filledElement = { ...element };
  if (elementSchema.widget === "single_select") {
    const labels = elementSchema.enumLabels || {};
    filledElement.type = "dropDown";
    (filledElement as DropDownInput).elements =
      elementSchema.enum?.map((enumValue) => ({
        key: `"${enumValue}"` as string,
        value: labels[enumValue as string] || (enumValue as string),
      })) || [];
  } else if (
    isInputComplexType(elementSchema) &&
    elementSchema.renderAsField === true
  ) {
    filledElement.type = "mappedComplexType";
  }
  filledElement.displayName = elementSchema.title || capitalize(elementKey);
  filledElement.hint = elementSchema.description || undefined;
  filledElement.rules = {
    ...filledElement.rules,
    maxLength: elementSchema.maxLength ?? elementSchema.maxItems,
    minLength: elementSchema.minLength ?? elementSchema.minItems,
    required: requiredElements?.includes(elementKey),
  };
  filledElement.beType = elementSchema.type;
  filledElement.example = elementSchema.example;
  return filledElement;
};

const fillUngroupedInputMappingFromManifest = (
  inputMappings: InputMappings,
  manifestNodeInputs: ManifestJSONSchema,
): {
  [key: string]: InputMapping;
} => {
  const ungrouped: {
    [key: string]: InputMapping;
  } = {};

  if (!manifestNodeInputs.properties) return {};

  Object.entries(manifestNodeInputs.properties!).forEach(
    ([inputKey, inputSchema]) => {
      if (typeof inputSchema !== "object" || inputSchema === null) return;

      if (!inputMappings.ungrouped[inputKey]) return;

      ungrouped[inputKey] = fillElementFromManifest(
        inputKey,
        inputMappings.ungrouped[inputKey],
        inputSchema,
        manifestNodeInputs.required,
      );
    },
  );

  return ungrouped;
};

const fillGroupElementsFromManifest = (
  elements: { [key: string]: InputMapping },
  manifestGroupDefinition: ManifestJSONSchema,
): { [key: string]: InputMapping } => {
  const groupElements: { [key: string]: InputMapping } = {};

  if (!manifestGroupDefinition.properties) return {};

  Object.entries(manifestGroupDefinition.properties!).forEach(
    ([elementKey, elementSchema]) => {
      if (typeof elementSchema !== "object" || elementSchema === null) return;
      if (!elements[elementKey]) return;

      groupElements[elementKey] = fillElementFromManifest(
        elementKey,
        elements[elementKey],
        elementSchema,
        manifestGroupDefinition.required,
      );
    },
  );
  return groupElements;
};

const fillGroupedInputMappingsFromManifest = (
  inputMappings: InputMappings,
  manifestNodeInputs: ManifestJSONSchema,
): { [key: string]: InputMappingGroup } => {
  const grouped = getDefaultManifestInputGroups(
    manifestNodeInputs,
    inputMappings.grouped,
  );

  Object.keys(grouped).forEach((groupedKey) => {
    const manifestGroupDefinition = manifestNodeInputs.properties![groupedKey];
    if (
      typeof manifestGroupDefinition !== "object" ||
      manifestGroupDefinition === null
    ) {
      return;
    }

    grouped[groupedKey] = {
      ...grouped[groupedKey],
      displayName: manifestGroupDefinition.title || capitalize(groupedKey),
      description: manifestGroupDefinition.description,
      rules: { required: manifestNodeInputs.required?.includes(groupedKey) },
      getDefaultElements: () =>
        getDefaultManifestInputUngrouped(
          manifestGroupDefinition as ManifestJSONSchema,
        ),
    };

    if (grouped[groupedKey].elements) {
      grouped[groupedKey].elements = fillGroupElementsFromManifest(
        grouped[groupedKey].elements!,
        manifestGroupDefinition,
      );
    }
  });
  return grouped;
};

const fillListInputMappingFromManifest = (
  inputMappings: InputMappings,
  manifestNodeInputs: ManifestJSONSchema,
): {
  [listName: string]: InputMappingList;
} => {
  const listInputs: {
    [listName: string]: InputMappingList;
  } = {};

  if (!manifestNodeInputs.properties) return {};

  Object.entries(manifestNodeInputs.properties!).forEach(
    ([listKey, manifestInputDefinition]) => {
      if (!(listKey in inputMappings.lists)) {
        return;
      }

      if (
        typeof manifestInputDefinition !== "object" ||
        manifestInputDefinition === null ||
        // Here we only fill the array properties from the manifest
        !isFieldOfNullableType(manifestInputDefinition, "array")
      ) {
        return;
      }

      const manifestListItemDefinition =
        manifestInputDefinition.items as ManifestJSONSchema;

      if (
        typeof manifestListItemDefinition !== "object" ||
        manifestListItemDefinition === null
      ) {
        return;
      }

      listInputs[listKey] = {
        ...inputMappings.lists[listKey],
        rules: {
          ...inputMappings.lists[listKey].rules,
          required: manifestNodeInputs.required?.includes(listKey),
        },
        displayName: manifestInputDefinition.title || capitalize(listKey),
        description: manifestInputDefinition.description,
        getDefaultElement: () =>
          getDefaultManifestInputUngrouped(manifestListItemDefinition),
      };

      if (!manifestListItemDefinition.properties) return;

      listInputs[listKey].elements = inputMappings.lists[listKey].elements.map(
        (listElement) => {
          const elementObj: { [key: string]: InputMapping } = {};

          Object.entries(manifestListItemDefinition.properties!).forEach(
            ([elementKey, elementSchema]) => {
              if (typeof elementSchema !== "object" || elementSchema === null)
                return;

              elementObj[elementKey] = fillElementFromManifest(
                elementKey,
                listElement[elementKey],
                elementSchema,
                manifestListItemDefinition.required,
              );
            },
          );

          return elementObj;
        },
      );
    },
  );
  return listInputs;
};

export const fillInputMappingFromManifest = (
  manifest: ManifestIntegrationProvider,
  integrationResource: ManifestIntegrationResource,
): InputMappings => {
  // The BE doesn't hold metadata (e.g. display name, rules) for the inputMappings.
  // This function fills the inputMappings with these metadata from the manifest.
  const inputMapping = integrationResource.input;
  const resource = integrationResource.providerResource.resource;
  const manifestNodeInputs = manifest.resources[resource].node_inputs;

  if (manifestNodeInputs && manifestNodeInputs.properties !== undefined) {
    inputMapping.ungrouped = fillUngroupedInputMappingFromManifest(
      inputMapping,
      manifestNodeInputs,
    );
    inputMapping.grouped = fillGroupedInputMappingsFromManifest(
      inputMapping,
      manifestNodeInputs,
    );
    inputMapping.lists = fillListInputMappingFromManifest(
      inputMapping,
      manifestNodeInputs,
    );
  }
  return inputMapping;
};

const pythonToJSONSchemaTypes: Record<string, string> = {
  str: "string",
  int: "integer",
  float: "number",
  bool: "boolean",
  dict: "object",
  list: "array",
  null: "null",
};

export const isResourceSFTP = (
  resource: ManifestResourceDefinition,
): boolean => {
  if (!resource.request_config) return false;
  if (!resource.request_config.type) return false;
  return resource.request_config.type === "generic_sftp";
};

export const fillOutputMappingFromManifest = (
  manifest: ManifestIntegrationProvider,
  integrationResource: ManifestIntegrationResource,
): OutputMappings => {
  // The output display name and description are added from the
  // manifest to complete the output mappings
  const unfilledOutputMapping = integrationResource.output;
  const resource = integrationResource.providerResource.resource;
  const manifestResource = manifest.resources[resource];
  const manifestInsights = manifestResource.insights;

  const isNonHTTP = isResourceSFTP(manifestResource);

  const filledOutputMapping: OutputMappings = {
    [DEFAULT_OUTPUT_BE_NAME]: {
      ...unfilledOutputMapping[DEFAULT_OUTPUT_BE_NAME],
      displayName: isNonHTTP
        ? NON_HTTP_BUNDLE_RESPONSE_TITLE
        : unfilledOutputMapping[DEFAULT_OUTPUT_BE_NAME].displayName,
      hint: isNonHTTP
        ? NON_HTTP_BUNDLE_RESPONSE_HINT
        : unfilledOutputMapping[DEFAULT_OUTPUT_BE_NAME].hint,
    },
    insights: {
      [CommonOutputMappingKey.PROVIDER_RESPONSE_TIME]:
        unfilledOutputMapping.insights[
          CommonOutputMappingKey.PROVIDER_RESPONSE_TIME
        ],
    },
  };

  Object.entries(manifestInsights).forEach(([insightKey, insightConfig]) => {
    if (typeof insightConfig !== "object" || insightConfig === null) return;

    if (!unfilledOutputMapping.insights[insightKey]) return;

    filledOutputMapping.insights[insightKey] = {
      ...unfilledOutputMapping.insights[insightKey],
      displayName: insightConfig.display_name || capitalize(insightKey),
      hint: insightConfig.description || undefined,
      type: insightConfig.coerce_type
        ? pythonToJSONSchemaTypes[insightConfig.coerce_type] ||
          insightConfig.coerce_type
        : undefined,
    };
  });

  return filledOutputMapping;
};

export const fillFilesMappingFromManifest = (
  files: { [key: string]: OutputMapping },
  fileExtraction: FileExtractionConfig,
): { [key: string]: OutputMapping } => {
  Object.entries(fileExtraction || {}).forEach(([key, value]) => {
    files[key] = {
      ...files[key],
      displayName: value.display_name || capitalize(key),
      hint: value.description || undefined,
    };
  });
  return files;
};
